<template>
  <div class="message-item">
    <div class="comment-content-wrapper">
      <img
        :src="message.avatar || PLACEHOLDER_ASSETS.defaultAvatar"
        alt="用户头像"
        class="comment-avatar"
      />

      <div class="comment-main">
        <p class="comment-author">{{ message.userName }}</p>
        <p class="comment-text">{{ message.content }}</p>

        <div class="comment-actions">
          <div class="comment-actions-left">
            <span class="comment-time">{{ message.addTime }}</span>
            <span class="comment-reply-btn" @click="toggleReplyBox"> 回复 </span>
            <span
              v-if="message.isBelongMe"
              class="message-delete-btn"
              @click.stop="deleteRootMessage"
            >
              删除
            </span>
          </div>
          <div class="comment-actions-right">
            <span class="comment-like" @click="handleLikeMessage">
              <AppIcon
                name="like"
                size="sm"
                :variant="message.isMyLike ? 'active' : 'default'"
                aria-label="点赞"
              />
              <span>{{ message.likeCount || 0 }}</span>
              <span v-if="likeEffect" class="like-effect"> +1 </span>
            </span>
          </div>
        </div>

        <div v-if="showReplyBox" class="comment-reply-box">
          <textarea
            v-model="replyContent"
            class="comment-reply-input"
            placeholder="请输入回复内容..."
          ></textarea>
          <div class="comment-reply-actions">
            <button
              class="comment-reply-submit"
              :disabled="loadingReply"
              @click="submitReply"
            >
              {{ loadingReply ? '提交中...' : '提交' }}
            </button>
            <button class="comment-reply-cancel" @click="toggleReplyBox">
              取消
            </button>
          </div>
        </div>

        <div
          v-if="message.existReply"
          class="comment-expand-reply"
          @click="toggleCommentReplies"
        >
          {{ message.showReplies ? '收起' : '展开'
          }}{{ message.replyCount || 0 }}条回复
        </div>

        <div
          v-if="
            message.showReplies && message.replies && message.replies.length > 0
          "
          class="comment-replies-list"
        >
          <div
            v-for="reply in showAllReplies
              ? message.replies
              : message.replies.slice(0, 3)"
            :key="reply.messageId"
            class="comment-reply-item"
          >
            <div class="comment-reply-header">
              <img
                :src="reply.avatar || PLACEHOLDER_ASSETS.defaultAvatar"
                alt="用户头像"
                class="comment-reply-avatar"
              />
              <span class="comment-reply-author">{{ reply.userName }}</span>
              <span v-if="reply.toUserName" class="comment-reply-to-info">
                <span class="comment-reply-to-text">回复</span>
                <span class="comment-reply-to-name">{{ reply.toUserName }}</span>
              </span>
            </div>

            <p class="comment-reply-content">{{ reply.content }}</p>

            <div class="comment-reply-actions">
              <div class="comment-reply-actions-left">
                <span class="comment-reply-time">{{ reply.replyTime }}</span>
                <span
                  class="comment-reply-btn-inline"
                  @click="toggleReplyBoxForReply(reply.messageId)"
                >
                  回复
                </span>
                <span
                  v-if="reply.isBelongMe"
                  class="message-delete-btn message-delete-btn--inline"
                  @click.stop="deleteGuestbookReply(reply)"
                >
                  删除
                </span>
              </div>
              <div class="comment-reply-actions-right">
                <span class="comment-reply-like" @click="likeReply(reply)">
                  <AppIcon
                    name="like"
                    size="sm"
                    :variant="reply.isMyLike ? 'active' : 'default'"
                    aria-label="点赞回复"
                  />
                  <span>{{ reply.likeCount || 0 }}</span>
                  <span v-if="likeEffectMap[reply.messageId]" class="like-effect">
                    +1
                  </span>
                </span>
              </div>
            </div>

            <div
              v-if="showReplyBoxMap[reply.messageId]"
              class="comment-reply-box"
            >
              <textarea
                v-model="replyContentMap[reply.messageId]"
                class="comment-reply-input"
                placeholder="请输入回复内容..."
              ></textarea>
              <div class="comment-reply-actions">
                <button
                  class="comment-reply-submit"
                  :disabled="loadingRepliesMap[reply.messageId]"
                  @click="submitReplyForReply(reply)"
                >
                  {{ loadingRepliesMap[reply.messageId] ? '提交中...' : '提交' }}
                </button>
                <button
                  class="comment-reply-cancel"
                  @click="toggleReplyBoxForReply(reply.messageId)"
                >
                  取消
                </button>
              </div>
            </div>
          </div>

          <div v-if="message.replies.length > 3" class="comment-reply-expand">
            <span class="comment-reply-expand-btn" @click="toggleShowAllReplies">
              {{ showAllReplies ? '收起' : '展开更多' }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { toRef } from 'vue'
import { useMessageItem } from '@/features/message/composables/useMessageItem'
import AppIcon from '@/shared/components/AppIcon.vue'
import { PLACEHOLDER_ASSETS } from '@/shared/assets/manifest'

const props = defineProps({
  message: {
    type: Object,
    required: true,
    default: () => ({
      replies: [],
      showReplies: false,
    }),
  },
})

const emit = defineEmits([
  'show-popup-message',
  'show-login-popup',
  'message-deleted',
])

const {
  showReplyBox,
  replyContent,
  loadingReply,
  likeEffect,
  showAllReplies,
  replyContentMap,
  showReplyBoxMap,
  loadingRepliesMap,
  likeEffectMap,
  handleLikeMessage,
  likeReply,
  toggleCommentReplies,
  toggleReplyBox,
  toggleReplyBoxForReply,
  submitReply,
  submitReplyForReply,
  toggleShowAllReplies,
  deleteRootMessage,
  deleteGuestbookReply,
} = useMessageItem(toRef(props, 'message'), emit)
</script>

<style scoped src="@/features/message/styles/MessageItem.css"></style>
